"use client";

import { useMemo, useState } from "react";

export interface FAQItem {
  question: string;
  answer: string;
}

const DEFAULT_FAQS: FAQItem[] = [
  {
    question: "Are the sessions live or recorded?",
    answer:
      "Most batches are delivered live by instructors, and session recordings are shared for revision after class.",
  },
  {
    question: "Do I need prior experience for this course?",
    answer:
      "No. Many of our courses are beginner-friendly and include structured guidance from fundamentals to advanced topics.",
  },
  {
    question: "Is placement support included?",
    answer:
      "Yes. We provide placement support that may include resume guidance, interview preparation, and job assistance based on your course track.",
  },
  {
    question: "How long do I have access to course materials?",
    answer:
      "You will receive extended access to class materials and recordings so you can revisit concepts even after course completion.",
  },
  {
    question: "Can I get a refund if I'm not satisfied?",
    answer:
      "Refund terms depend on your enrollment plan and timing. Please contact support for exact eligibility and policy details.",
  },
  {
    question: "Do you offer weekend or evening batches?",
    answer:
      "Yes. We run both weekday and weekend/evening batches to accommodate working professionals and students.",
  },
];

export interface FAQWidgetProps {
  faqs?: FAQItem[];
  generateSchema?: boolean;
  allowMultiple?: boolean;
  className?: string;
  title?: string;
  subtitle?: string;
}

export default function FAQWidget({
  faqs = [],
  generateSchema = false,
  allowMultiple = false,
  className = "",
  title = "Frequently Asked Questions",
  subtitle = "",
}: FAQWidgetProps) {
  const normalizedFaqs = useMemo<FAQItem[]>(() => {
    const source = Array.isArray(faqs) && faqs.length > 0 ? faqs : DEFAULT_FAQS;

    return source
      .filter((item) => item?.question && item?.answer)
      .map((item) => ({
        question: String(item.question),
        answer: String(item.answer),
      }));
  }, [faqs]);

  const [openIndexes, setOpenIndexes] = useState<number[]>(() =>
    normalizedFaqs.length > 0 ? [0] : []
  );

  const toggleItem = (index: number) => {
    setOpenIndexes((prev) => {
      const isOpen = prev.includes(index);

      if (allowMultiple) {
        if (isOpen) {
          return prev.filter((item) => item !== index);
        }

        return [...prev, index];
      }

      if (isOpen) {
        return [];
      }

      return [index];
    });
  };

  const faqSchema = useMemo(() => {
    if (!generateSchema || normalizedFaqs.length === 0) {
      return null;
    }

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: normalizedFaqs.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    };
  }, [generateSchema, normalizedFaqs]);

  return (
    <section className={`bg-[#F0F4FF] py-16 md:py-20 ${className}`.trim()}>
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="text-center mb-12">
          <h2 className="text-[#1A1A1A] text-3xl md:text-4xl font-bold mb-4">{title}</h2>
          {subtitle ? (
            <p className="text-[#666666] text-[18px] leading-relaxed max-w-3xl mx-auto">
              {subtitle}
            </p>
          ) : null}
        </div>

        <div className="bg-white rounded-2xl overflow-hidden divide-y divide-[#E5E7EB] shadow-sm">
          {normalizedFaqs.map((item, index) => {
            const isOpen = openIndexes.includes(index);

            return (
              <article key={`${item.question}-${index}`}>
                <button
                  type="button"
                  onClick={() => toggleItem(index)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer group"
                >
                  <span
                    className={`text-[20px] font-semibold leading-snug pr-4 transition-colors ${
                      isOpen ? "text-[#1A1A1A]" : "text-[#1A1A1A] group-hover:text-[#1B61FF]"
                    }`}
                  >
                    {item.question}
                  </span>
                  <span className="text-2xl leading-none font-medium text-[#666666]" aria-hidden="true">
                    {isOpen ? "−" : "+"}
                  </span>
                </button>

                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 text-[#666666] text-[18px] leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {faqSchema ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      ) : null}
    </section>
  );
}

